import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import {
  useGetInventoryStatusDetailBySchoolIdQuery,
  useCreateInventoryStatus,
  useUpdateInventoryStatus,
} from "@/services/school";
import {
  inventoryStatusSchema,
  InventoryStatusFormValues,
} from "./validations";
import { taskStatus } from "@/lib/utils";
import { toast } from "sonner";

const DEFAULT_VALUES: InventoryStatusFormValues = {
  packingDetails: [
    {
      packingStatus: "",
      approvedBy: "",
      shipmentDate: "",
      inventoryCheckLists: [],
    },
  ],
  note: "",
};

const useInventoryStatusDetails = () => {
  const { id } = useParams();
  const schoolId = String(id || "");

  /* ---------------- Form ---------------- */
  const form = useForm<InventoryStatusFormValues>({
    resolver: zodResolver(inventoryStatusSchema),
    defaultValues: DEFAULT_VALUES,
  });

  /* ---------------- APIs ---------------- */
  const { mutateAsync: createInventoryStatus } = useCreateInventoryStatus();
  const { mutateAsync: updateInventoryStatus } = useUpdateInventoryStatus();

  const { data, isLoading, refetch } =
    useGetInventoryStatusDetailBySchoolIdQuery(schoolId);

  const inventoryStatusData = data?.data;

  /* ---------------- Prefill ---------------- */
  useEffect(() => {
    if (!inventoryStatusData) return;

    const approvedByMap = Object.fromEntries(
      (inventoryStatusData.approvedByDetails || []).map((d: any) => [
        d.profile_id,
        d.name,
      ])
    );

    form.reset({
      packingDetails:
        inventoryStatusData.inventoryStatusList?.map((item: any) => ({
          id: item._id,
          packingStatus: item.packingStatus || "",
          approvedBy: item.approvedBy || "",
          shipmentDate: item.shipmentDate?.split("T")[0] || "",
          approvedByLabel: approvedByMap[item.approvedBy] || "",
          inventoryCheckLists:
            item.inventoryCheckLists.map((v: any) => ({_id: v._id, comment: v.comments})) || [],
        })) || DEFAULT_VALUES.packingDetails,
      note: inventoryStatusData.note || "",
    });
  }, [inventoryStatusData]);

  /* ---------------- Submit ---------------- */
  const onSubmitInventoryStatus = async (values: InventoryStatusFormValues) => {
    const inventoryStatusList = values.packingDetails.map((item) => ({
      id: item.id,
      packingStatus: item.packingStatus,
      approvedBy: item.approvedBy,
      shipmentDate: item.shipmentDate,
      inventoryCheckLists: item.inventoryCheckLists?.map((v: any) => ({_id: v._id, comment: v.comments || v.comment})),
    }));

    const payload = {
      inventoryStatusList,
      note: values.note || "",
    };

    if (inventoryStatusData?._id) {
      await updateInventoryStatus({ schoolId, payload });
    } else {
      await createInventoryStatus({ schoolId, ...payload });
    }

    // 1. refetch server data
    const latest = await refetch();

    // 2. SYNC RHF WITH LATEST DATA
    if (latest?.data?.data) {
      form.reset({
        packingDetails: latest.data.data.inventoryStatusList || [],
        note: latest.data.data.note || "",
      });
    }
  };

  /* ---------------- Delete ---------------- */
  const handleDeleteInventoryStatus = async (index: number) => {
    const items = form.getValues("packingDetails");
    console.log({ items });

    const item = items[index];

    const apiStatus =
      inventoryStatusData?.inventoryStatusList?.[index]?.packingStatus;

    if (apiStatus === taskStatus.Completed.value) {
      toast.error("Completed packing cannot be modified");
      return;
    }

    // Local delete for new rows
    if (!item?.id) {
      form.setValue(
        "packingDetails",
        items.filter((_, i) => i !== index)
      );
      return;
    }

    // API delete for existing rows
    await updateInventoryStatus({
      schoolId,
      payload: {
        inventoryStatusList: [
          {
            id: item.id,
            isDeleted: true,
            packingStatus: item.packingStatus,
            approvedBy: item.approvedBy,
            shipmentDate: item.shipmentDate,
            inventoryCheckLists: item.inventoryCheckLists.map((v: any) => ({_id: v._id, comment: v.comments || v.comment})),
          },
        ],
        note: form.getValues("note") || "",
      },
    });

    await refetch();
  };

  return {
    inventoryStatusForm: form,
    inventoryStatusData,
    isLoadingInventoryStatus: isLoading,
    onSubmitInventoryStatus,
    handleDeleteInventoryStatus,
    refetchInventoryStatus: refetch,
  };
};

export default useInventoryStatusDetails;
